import { api } from './client';

type Envelope<T> = { data: T };

export const QUESTION_TYPES = [
  'short_text',
  'long_text',
  'number',
  'date',
  'single_choice',
  'multi_choice',
  'dropdown',
  'yes_no',
  'yes_no_na',
  'rating',
] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

/** Types where the author supplies the option list. */
export const OPTION_TYPES: QuestionType[] = ['single_choice', 'multi_choice', 'dropdown'];

export type ChecklistQuestion = {
  id: string;
  label: string;
  help?: string;
  type: QuestionType;
  required: boolean;
  options?: string[];
  max?: number;
  /** Show a free-text "why?" box under the answer. */
  allowComment?: boolean;
  /** Answers that make that comment mandatory (e.g. ["No"]). */
  commentRequiredFor?: string[];
};

/**
 * Comments live in the same answers map under a reserved key — the server
 * validates them there and no question id may start with "#", so the two
 * namespaces cannot collide. Mirrors backend checklist-schema.ts.
 */
export const COMMENT_KEY_PREFIX = '#c:';
export const commentKey = (questionId: string): string => `${COMMENT_KEY_PREFIX}${questionId}`;
export const answerComment = (questionId: string, answers: ChecklistAnswers): string => {
  const v = answers[commentKey(questionId)];
  return typeof v === 'string' ? v : '';
};

export type ChecklistSection = {
  id: string;
  title: string;
  description?: string;
  questions: ChecklistQuestion[];
};

export type ChecklistSchema = { sections: ChecklistSection[] };

export type ChecklistAnswers = Record<string, string | string[] | number | null>;

export type ChecklistTemplate = {
  id: string;
  title: string;
  description: string | null;
  status: 'pending_approval' | 'active' | 'archived';
  schema: ChecklistSchema;
  questionCount: number;
  envelopeId: string | null;
  approvedAt: string | null;
  /** Approved template whose questionnaire was re-authored afterwards. */
  editedAfterApproval: boolean;
  createdAt: string;
  updatedAt: string;
  creator: { id: string; firstName: string; lastName: string; email: string } | null;
  responseCount?: number;
};

export type ChecklistResponse = {
  id: string;
  templateId: string;
  templateTitle: string;
  title: string;
  status: 'draft' | 'submitted';
  schema: ChecklistSchema;
  answers: ChecklistAnswers;
  externalSystem: string | null;
  externalRegister: string | null;
  externalRowId: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  filledBy: { id: string; firstName: string; lastName: string; email: string } | null;
};

export const checklistsApi = {
  async listTemplates(params: { status?: string; search?: string } = {}): Promise<ChecklistTemplate[]> {
    const { data } = await api.get<Envelope<ChecklistTemplate[]>>('/checklists/templates', { params });
    return data.data;
  },

  async getTemplate(uuid: string): Promise<ChecklistTemplate> {
    const { data } = await api.get<Envelope<ChecklistTemplate>>(`/checklists/templates/${uuid}`);
    return data.data;
  },

  async getByEnvelope(envelopeId: string): Promise<ChecklistTemplate | null> {
    const { data } = await api.get<Envelope<ChecklistTemplate | null>>(
      `/checklists/envelope/${envelopeId}`,
    );
    return data.data;
  },

  async createForEnvelope(
    envelopeId: string,
    payload: { title: string; description?: string; schema: ChecklistSchema },
  ): Promise<{ template: ChecklistTemplate; documentId: string }> {
    const { data } = await api.post<Envelope<{ template: ChecklistTemplate; documentId: string }>>(
      `/checklists/envelope/${envelopeId}`,
      payload,
      // The blank form is rendered to PDF server-side before we get a reply.
      { timeout: 60_000 },
    );
    return data.data;
  },

  async updateTemplate(
    uuid: string,
    payload: { title: string; description?: string; schema: ChecklistSchema },
  ): Promise<ChecklistTemplate> {
    const { data } = await api.put<Envelope<ChecklistTemplate>>(
      `/checklists/templates/${uuid}`,
      payload,
      { timeout: 60_000 },
    );
    return data.data;
  },

  async setTemplateStatus(uuid: string, status: 'active' | 'archived'): Promise<ChecklistTemplate> {
    const { data } = await api.patch<Envelope<ChecklistTemplate>>(
      `/checklists/templates/${uuid}/status`,
      { status },
    );
    return data.data;
  },

  async deleteTemplate(uuid: string): Promise<void> {
    await api.delete(`/checklists/templates/${uuid}`);
  },

  async listResponses(
    params: { system?: string; register?: string; row?: string; template?: string } = {},
  ): Promise<ChecklistResponse[]> {
    const { data } = await api.get<Envelope<ChecklistResponse[]>>('/checklists/responses', { params });
    return data.data;
  },

  async getResponse(uuid: string): Promise<ChecklistResponse> {
    const { data } = await api.get<Envelope<ChecklistResponse>>(`/checklists/responses/${uuid}`);
    return data.data;
  },

  async createResponse(payload: {
    templateId: string;
    title?: string;
    answers?: ChecklistAnswers;
    submit?: boolean;
    externalSystem?: string;
    externalRegister?: string;
    externalRowId?: string;
  }): Promise<ChecklistResponse> {
    const { data } = await api.post<Envelope<ChecklistResponse>>('/checklists/responses', payload);
    return data.data;
  },

  async updateResponse(
    uuid: string,
    payload: { title?: string; answers?: ChecklistAnswers; submit?: boolean },
  ): Promise<ChecklistResponse> {
    const { data } = await api.put<Envelope<ChecklistResponse>>(
      `/checklists/responses/${uuid}`,
      payload,
    );
    return data.data;
  },

  async deleteResponse(uuid: string): Promise<void> {
    await api.delete(`/checklists/responses/${uuid}`);
  },

  async downloadTemplateXlsx(uuid: string, filename: string): Promise<void> {
    const res = await api.get<Blob>(`/checklists/templates/${uuid}/export.xlsx`, {
      responseType: 'blob',
    });
    triggerBlobDownload(res.data, filename);
  },

  async downloadResponseXlsx(uuid: string, filename: string): Promise<void> {
    const res = await api.get<Blob>(`/checklists/responses/${uuid}/export.xlsx`, {
      responseType: 'blob',
    });
    triggerBlobDownload(res.data, filename);
  },
};

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
